/**
 * Pipeline Models — per-pipeline model control (P3-C redesign).
 *
 * The old "Tier defaults" bar and per-pipeline Edit dialog are gone. Each
 * pipeline now exposes three inline dropdowns and a Use Default button
 * (PipelineRow):
 *   - xAI (Grok) + Anthropic (Claude) are mutually-exclusive selectors for the
 *     ACTIVE model → PUT /pipeline-models/:key.
 *   - Default → PUT /pipeline-models/:key/default.
 *   - Use Default → POST /pipeline-models/:key/use-default (active := default).
 *
 * Every write hits the production Quill DB, so all four intents route through a
 * single production-confirm step (QuillActionConfirm). The two vulnerable-user
 * safety pipelines (guardrails_classifier / refusal_generation) additionally
 * return 428 on the first attempt; we then surface the typed confirm_safety
 * step and re-send, and the G3 SafetyReverificationBanner still renders the
 * pending re-verification reminders.
 *
 * Layout, top → bottom:
 *   1. Pending-safety banner (GET /safety-reverifications?pending=true).
 *   2. Pipeline board — a sticky filter bar over seven cost-center groups, each
 *      group a stack of collapsed summary lines that expand INLINE to the editor
 *      above. Group collapse persists to localStorage (default expanded); row
 *      expansion is page-level state (below) so a write's refetch cannot close
 *      the row an operator is working in.
 *
 * Each section loads independently (Promise.allSettled) so one failing endpoint
 * never blanks the page.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Snackbar, Alert, TextField, Tooltip, Button } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useQuillHealth } from '../QuillHealthContext';
import {
  quillApi,
  QuillApiError,
  ModelCatalogRow,
  PipelineModel,
  PipelineModelRef,
  PipelineHistoryRow,
  PipelineResetResponse,
  SafetyReverification,
  SetPipelineModelRequest,
} from '../../../services/quillAdminService';
import { colors, typography } from '../../../theme/designTokens';
import { HelpTip } from '../../help/HelpTip';
import QuillActionConfirm from '../common/QuillActionConfirm';
import PipelineModelsTable from './PipelineModelsTable';
import PipelineHistoryPanel from './PipelineHistoryPanel';
import SafetyReverificationBanner from './SafetyReverificationBanner';
import ResetAllToDefaultDialog, { resetHeadline } from './ResetAllToDefaultDialog';
import { CounterbalanceBanner } from './CounterbalanceNotice';

type Severity = 'success' | 'info' | 'error' | 'warning';

/** A staged production write awaiting confirmation. */
type PendingAction =
  | { kind: 'active'; pipeline: PipelineModel; ref: PipelineModelRef }
  | { kind: 'default'; pipeline: PipelineModel; ref: PipelineModelRef }
  | { kind: 'use-default'; pipeline: PipelineModel }
  | { kind: 'revert'; pipeline: PipelineModel };

const PipelineModelsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Snackbar ────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState<{ message: string; severity: Severity } | null>(null);
  const notify = useCallback((message: string, severity: Severity = 'success') => {
    setSnackbar({ message, severity });
  }, []);

  // ── Model catalog (shared by every row's dropdowns) ──────────────────
  const [catalog, setCatalog] = useState<ModelCatalogRow[]>([]);
  const loadCatalog = useCallback(async () => {
    try {
      const resp = await quillApi.models.getAvailable();
      if (mountedRef.current) setCatalog(resp.data);
    } catch {
      // Non-fatal — dropdowns simply show no eligible models; rows still render.
      if (mountedRef.current) setCatalog([]);
    }
  }, []);

  // ── Pipelines ────────────────────────────────────────────────────────
  const [pipelines, setPipelines] = useState<PipelineModel[]>([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(true);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);
  const loadPipelines = useCallback(async () => {
    setPipelinesLoading(true);
    setPipelinesError(null);
    try {
      const rows = await quillApi.pipelineModels.list();
      if (mountedRef.current) setPipelines(rows);
    } catch (e) {
      if (mountedRef.current) setPipelinesError(e instanceof Error ? e.message : 'Failed to load pipelines.');
    } finally {
      if (mountedRef.current) setPipelinesLoading(false);
    }
  }, []);

  // ── Row expansion (page-level, keyed by pipeline_key) ────────────────
  // A successful write refetches the whole pipeline list. Holding expansion here
  // rather than inside PipelineRow means the row the operator just edited stays
  // open across that refetch; the ref mirror keeps the record even if the list
  // component itself is remounted (error → retry, filter reset).
  const expandedRowsRef = useRef<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(expandedRowsRef.current);
  const toggleRow = useCallback((pipelineKey: string) => {
    const next = { ...expandedRowsRef.current, [pipelineKey]: !expandedRowsRef.current[pipelineKey] };
    expandedRowsRef.current = next;
    setExpandedRows(next);
  }, []);

  // ── Pending safety reverifications ───────────────────────────────────
  const [pendingSafety, setPendingSafety] = useState<SafetyReverification[]>([]);
  const loadPendingSafety = useCallback(async () => {
    try {
      const rows = await quillApi.safety.listPending();
      if (mountedRef.current) setPendingSafety(rows);
    } catch {
      if (mountedRef.current) setPendingSafety([]);
    }
  }, []);

  useEffect(() => {
    Promise.allSettled([loadCatalog(), loadPipelines(), loadPendingSafety()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Production-confirm + safety orchestration ────────────────────────
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [safetyStep, setSafetyStep] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const stage = useCallback((action: PendingAction) => {
    setPending(action);
    setSafetyStep(false);
    setReason('');
  }, []);

  const onSetActive = useCallback(
    (pipeline: PipelineModel, ref: PipelineModelRef) => stage({ kind: 'active', pipeline, ref }),
    [stage]
  );
  const onSetDefault = useCallback(
    (pipeline: PipelineModel, ref: PipelineModelRef) => stage({ kind: 'default', pipeline, ref }),
    [stage]
  );
  const onUseDefault = useCallback(
    (pipeline: PipelineModel) => stage({ kind: 'use-default', pipeline }),
    [stage]
  );
  const onRevert = useCallback(
    (pipeline: PipelineModel) => stage({ kind: 'revert', pipeline }),
    [stage]
  );

  const cancelPending = useCallback(() => {
    setPending(null);
    setSafetyStep(false);
    setReason('');
  }, []);

  const dispatchAction = useCallback(
    async (action: PendingAction, confirmSafety: boolean) => {
      const key = action.pipeline.pipeline_key;
      const payloadBase = (ref: PipelineModelRef): SetPipelineModelRequest => ({
        provider: ref.provider,
        model_id: ref.model_id,
        reason: reason.trim() || undefined,
        ...(confirmSafety ? { confirm_safety: true } : {}),
      });
      switch (action.kind) {
        case 'active':
          return quillApi.pipelineModels.setActive(key, payloadBase(action.ref));
        case 'default':
          return quillApi.pipelineModels.setDefault(key, payloadBase(action.ref));
        case 'use-default':
          return quillApi.pipelineModels.useDefault(key, confirmSafety);
        case 'revert':
          return quillApi.pipelineModels.revert(key, confirmSafety);
      }
    },
    [reason]
  );

  const successMessage = (action: PendingAction): string => {
    const name = action.pipeline.display_name;
    switch (action.kind) {
      case 'active':
        return `${name} active model → ${action.ref.provider}/${action.ref.model_id}`;
      case 'default':
        return `${name} default model → ${action.ref.provider}/${action.ref.model_id}`;
      case 'use-default':
        return `${name} active model reset to its default.`;
      case 'revert':
        return `Restored "${name}" to its previous model from the change log.`;
    }
  };

  const confirmAction = useCallback(
    async (confirmSafety: boolean) => {
      if (!pending) return;
      setSubmitting(true);
      try {
        await dispatchAction(pending, confirmSafety);
        notify(successMessage(pending), 'success');
        cancelPending();
        loadPipelines();
        loadPendingSafety(); // a safety-path write raises a new pending reminder
      } catch (e) {
        if (e instanceof QuillApiError && e.status === 428 && !confirmSafety) {
          setSafetyStep(true); // escalate to the typed re-verification step
        } else {
          notify(e instanceof Error ? e.message : 'The change failed.', 'error');
          cancelPending();
        }
      } finally {
        setSubmitting(false);
      }
    },
    [pending, dispatchAction, notify, cancelPending, loadPipelines, loadPendingSafety]
  );

  // ── Bulk reset to defaults ───────────────────────────────────────────
  // Opening the dialog fetches a server-side PREVIEW (dry_run) rather than
  // computing one here: the confirm list has to be the same list the write
  // path produces, and the only way to guarantee that is to ask the planner.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPreview, setResetPreview] = useState<PipelineResetResponse | null>(null);
  const [resetPreviewLoading, setResetPreviewLoading] = useState(false);
  const [resetPreviewError, setResetPreviewError] = useState<string | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetResult, setResetResult] = useState<PipelineResetResponse | null>(null);

  const openReset = useCallback(async () => {
    setResetOpen(true);
    setResetResult(null);
    setResetPreview(null);
    setResetPreviewError(null);
    setResetPreviewLoading(true);
    try {
      const preview = await quillApi.pipelineModels.resetAllToDefault({ dryRun: true });
      if (mountedRef.current) setResetPreview(preview);
    } catch (e) {
      if (mountedRef.current) {
        setResetPreviewError(e instanceof Error ? e.message : 'Could not work out what would change.');
      }
    } finally {
      if (mountedRef.current) setResetPreviewLoading(false);
    }
  }, []);

  const closeReset = useCallback(() => {
    setResetOpen(false);
    setResetPreview(null);
    setResetPreviewError(null);
    setResetResult(null);
  }, []);

  const confirmReset = useCallback(async () => {
    setResetSubmitting(true);
    try {
      const result = await quillApi.pipelineModels.resetAllToDefault({});
      if (!mountedRef.current) return;
      setResetResult(result);
      // The snackbar repeats the honest headline — never "all", always the
      // count that moved and the count that did not.
      notify(resetHeadline(result.summary, true), result.summary.failed > 0 ? 'warning' : 'success');
      loadPipelines();
    } catch (e) {
      if (mountedRef.current) {
        notify(e instanceof Error ? e.message : 'The reset failed.', 'error');
        closeReset();
      }
    } finally {
      if (mountedRef.current) setResetSubmitting(false);
    }
  }, [notify, loadPipelines, closeReset]);

  // ── History drawer ───────────────────────────────────────────────────
  const [historyPipeline, setHistoryPipeline] = useState<PipelineModel | null>(null);
  const [pipelineHistory, setPipelineHistory] = useState<PipelineHistoryRow[]>([]);
  const [pipelineHistoryLoading, setPipelineHistoryLoading] = useState(false);
  const [pipelineHistoryError, setPipelineHistoryError] = useState<string | null>(null);

  const loadPipelineHistory = useCallback(async (pipeline: PipelineModel) => {
    setPipelineHistoryLoading(true);
    setPipelineHistoryError(null);
    try {
      const rows = await quillApi.pipelineModels.getHistory(pipeline.pipeline_key);
      if (mountedRef.current) setPipelineHistory(rows);
    } catch (e) {
      if (mountedRef.current) {
        setPipelineHistoryError(e instanceof Error ? e.message : 'Failed to load pipeline history.');
      }
    } finally {
      if (mountedRef.current) setPipelineHistoryLoading(false);
    }
  }, []);

  const openPipelineHistory = useCallback(
    (pipeline: PipelineModel) => {
      setHistoryPipeline(pipeline);
      loadPipelineHistory(pipeline);
    },
    [loadPipelineHistory]
  );

  const handleAcknowledge = useCallback(
    async (id: string, note: string) => {
      await quillApi.safety.acknowledge(id, note);
      loadPendingSafety();
    },
    [loadPendingSafety]
  );

  // ── Confirm-dialog copy per action kind ──────────────────────────────
  const confirmCopy = useMemo(() => {
    if (!pending) return null;
    const key = pending.pipeline.pipeline_key;
    switch (pending.kind) {
      case 'active':
        return {
          title: `Change active model — ${pending.pipeline.display_name}`,
          message: `This re-points the LIVE active model for '${key}' at ${pending.ref.provider}/${pending.ref.model_id} (production Quill DB). The engine picks it up within ~60 seconds.`,
          confirmLabel: 'Change active model',
          withReason: true,
        };
      case 'default':
        return {
          title: `Set default model — ${pending.pipeline.display_name}`,
          message: `This sets the DEFAULT model for '${key}' to ${pending.ref.provider}/${pending.ref.model_id}. The default is what "Use Default" restores the active model to.`,
          confirmLabel: 'Set default',
          withReason: true,
        };
      case 'use-default':
        return {
          title: `Use default — ${pending.pipeline.display_name}`,
          message: `This sets the active model for '${key}' to its default (${pending.pipeline.default.provider}/${pending.pipeline.default.model_id}). The engine picks it up within ~60 seconds.`,
          confirmLabel: 'Use default model',
          withReason: false,
        };
      case 'revert':
        return {
          title: `Restore previous model — ${pending.pipeline.display_name}`,
          message: `This restores '${key}' to the previous model recorded in its change log (production Quill DB), undoing the most recent active change.`,
          confirmLabel: 'Restore previous model',
          withReason: false,
        };
    }
  }, [pending]);

  const reasonField = confirmCopy?.withReason ? (
    <TextField
      fullWidth
      size="small"
      multiline
      minRows={2}
      placeholder="Reason (optional — written to the change log)"
      value={reason}
      onChange={(e) => setReason(e.target.value)}
      inputProps={{ 'aria-label': 'Change reason' }}
    />
  ) : undefined;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <AccountTreeIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
              Pipeline Models
            </Typography>
            <HelpTip id="quill-pipeline-models" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
              Per-pipeline model control — set each pipeline's active model (xAI or Anthropic), its default, or reset to default.
            </Typography>
            <Tooltip
              arrow
              placement="right"
              title={
                <Box sx={{ p: 0.5, maxWidth: 300 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                    <strong>Active</strong> — the model the engine is using right now for this pipeline. Changing it takes effect within ~60 seconds.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                    <strong>Default</strong> — the pipeline's fallback of record. It doesn't change the live model on its own; it's the target that "Use Default" restores to.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    <strong>Use Default</strong> — re-points the active model back to the default (only enabled when the pipeline has been overridden away from it).
                  </Typography>
                </Box>
              }
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: 'text.disabled', cursor: 'help', flexShrink: 0 }}
                aria-label="What do active, default, and use default mean?"
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>

      {/* 1. Safety banner */}
      <SafetyReverificationBanner pending={pendingSafety} onAcknowledge={handleAcknowledge} notify={notify} />

      {/* 2. Counterbalance warning — a lost independent check, visible without
             opening a row. */}
      <CounterbalanceBanner pipelines={pipelines} />

      {/* 3. Pipeline list */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
          Pipelines
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {/* Secondary on purpose: this is a bulk production write, not the thing
            an operator came here to do. */}
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<RestartAltIcon />}
          onClick={openReset}
          disabled={pipelinesLoading || pipelines.length === 0}
          sx={{ textTransform: 'none', color: colors.gray[700], borderColor: colors.gray[300] }}
        >
          Reset all to default
        </Button>
      </Box>
      <PipelineModelsTable
        pipelines={pipelines}
        catalog={catalog}
        loading={pipelinesLoading}
        error={pipelinesError}
        onRetry={loadPipelines}
        expandedRows={expandedRows}
        onToggleRow={toggleRow}
        onSetActive={onSetActive}
        onSetDefault={onSetDefault}
        onUseDefault={onUseDefault}
        onRevert={onRevert}
        onHistory={openPipelineHistory}
      />

      {/* History drawer */}
      <PipelineHistoryPanel
        open={historyPipeline !== null}
        pipelineKey={historyPipeline?.pipeline_key ?? null}
        displayName={historyPipeline?.display_name ?? null}
        rows={pipelineHistory}
        loading={pipelineHistoryLoading}
        error={pipelineHistoryError}
        onRetry={() => historyPipeline && loadPipelineHistory(historyPipeline)}
        onClose={() => setHistoryPipeline(null)}
      />

      {/* Bulk reset — preview, confirm, and the outcome report */}
      <ResetAllToDefaultDialog
        open={resetOpen}
        previewLoading={resetPreviewLoading}
        preview={resetPreview}
        previewError={resetPreviewError}
        submitting={resetSubmitting}
        result={resetResult}
        onConfirm={confirmReset}
        onClose={closeReset}
      />

      {/* Production confirm (stage 1) */}
      <QuillActionConfirm
        open={pending !== null && !safetyStep}
        title={confirmCopy?.title ?? ''}
        message={confirmCopy?.message ?? ''}
        detail={reasonField}
        caption="This writes to the production Quill DB and affects real members."
        confirmLabel={confirmCopy?.confirmLabel ?? 'Confirm'}
        destructive
        loading={submitting}
        onConfirm={() => confirmAction(false)}
        onCancel={cancelPending}
      />

      {/* Safety re-verification (stage 2) */}
      <QuillActionConfirm
        open={safetyStep}
        title="Safety re-verification required"
        message={`'${pending?.pipeline.pipeline_key ?? ''}' is on the vulnerable-user safety path. This change requires a vulnerable-user re-verification (G3) before it is considered signed off.`}
        caption="Confirming records this change with confirm_safety and raises a pending re-verification reminder that must be acknowledged on this page."
        confirmLabel="I understand — apply the safety-path change"
        destructive
        loading={submitting}
        pendingLabel="Applying…"
        onConfirm={() => confirmAction(true)}
        onCancel={cancelPending}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default PipelineModelsPage;
